import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { logger } from "./utils/logging";
import { findBrandByDomain, findDomainData } from "./configs/configLoader";
import { Translations } from "./types/translations";

const urlParameters = new URLSearchParams(window.location.search);
const countryCode = urlParameters.get("countryCode");

const findedBrand = findBrandByDomain();

const brand = findedBrand
  ? findedBrand.name.toLowerCase().replace(/\s+/g, "")
  : "default";

const isDevernois = findedBrand?.name === "Devernois";

const isDevernoisEU = isDevernois && countryCode === "EU";

const domainData = findDomainData();

const namespaces = [
  "components/intro",
  "components/device_select",
  "components/qrcode/desktop",
  "components/qrcode/mobile",
  "components/belly",
  "components/torso",
  "components/cuisses",
  "components/breasts",
  "components/sleeves",
  "components/results/error",
  "components/results/result",
  "components/antiBracketing",
  "components/consentement",
  "components/lingerie-question",
];

const TRANSLATIONS_CACHE_KEY = "translations_cache";

const handleDefaultConfig = async (lng: string) => {
  const defaultPath = `/locales/default/${lng}.json`;

  try {
    const response = await fetch(defaultPath);
    if (response.ok) {
      logger.log(`⚠️ Fallback to default brand config (${lng})`);
      return await response.json();
    }
  } catch {
    logger.warn(`Could not load default translation for path: ${defaultPath}`);
  }

  return {};
};

const getBrandJson = async (
  brand: string,
  lng: string,
): Promise<{ data: Record<string, any>; isExactMatch: boolean }> => {
  const brandsToTry = [];

  brandsToTry.push(brand);

  const isKenzoUS = brand === "kenzo" && countryCode === "US";

  for (const b of brandsToTry) {
    const brandPath = isKenzoUS
      ? `/locales/${b}/en-us/${lng}.json`
      : `/locales/${b}/${lng}.json`;
    try {
      const response = await fetch(brandPath);
      if (response.ok) {
        logger.log(`✅ Loaded brand config: ${b} (${lng})`);
        return { data: await response.json(), isExactMatch: true };
      }
    } catch {
      logger.warn(`Could not load translation for path: ${brandPath}`);
    }

    if (lng !== "en") {
      const brandEnPath = `/locales/${b}/en.json`;
      try {
        const response = await fetch(brandEnPath);
        if (response.ok) {
          logger.log(`⚠️ Fallback to brand English config: ${b}`);
          return { data: await response.json(), isExactMatch: false };
        }
      } catch {
        logger.warn(`Could not load English fallback for brand: ${b}`);
      }
    }
  }

  const data = await handleDefaultConfig(lng);
  return { data, isExactMatch: true };
};

export async function loadTranslations(lang: string) {
  let lng = lang?.trim() || "fr";

  if (isDevernois && (lang === "fr" || lang === "es")) {
    lng = lang;
  } else if (isDevernoisEU || domainData?.unitSystem === "imperial") {
    lng = "en";
  }

  document.documentElement.lang = lng;

  const force_update = true;
  const cachedTranslations = localStorage.getItem(TRANSLATIONS_CACHE_KEY);

  if (cachedTranslations && !force_update) {
    try {
      const allTranslations = JSON.parse(cachedTranslations);
      addTranslationsToI18n(allTranslations, lng);
      return;
    } catch (error) {
      logger.error(`Error parsing cached translations: ${error}`);
    }
  }

  const translations: Translations = { default: {}, [brand]: {} };

  try {
    const { data, isExactMatch } = await getBrandJson(brand, lng);
    translations[brand][lng] = data;
    translations[brand]._isExactMatch = isExactMatch;

    if (brand !== "default") {
      const defaultPath = `/locales/default/${lng}.json`;
      const response = await fetch(defaultPath);
      if (response.ok) {
        const data = await response.json();
        translations.default[lng] = data;
      }
    }

    localStorage.setItem(TRANSLATIONS_CACHE_KEY, JSON.stringify(translations));
    addTranslationsToI18n(translations, lng);
  } catch (error) {
    logger.error(`Error loading translations: ${error}`);
  }
}

function addTranslationsToI18n(allTranslations: Translations, lng: string) {
  const defaultTranslations = allTranslations["default"] || {};
  let brandTranslations: Record<string, any> = allTranslations[brand] || {};
  const isExactMatch = brandTranslations._isExactMatch !== false;

  if (
    typeof brandTranslations === "object" &&
    Object.keys(brandTranslations).length === 0 &&
    brand !== "default"
  ) {
    const groupedKey = Object.keys(allTranslations).find((key) =>
      key.split("/").includes(brand),
    );
    brandTranslations = groupedKey ? allTranslations[groupedKey] : {};
  }

  for (const ns of namespaces) {
    const defaultNS = defaultTranslations[lng]?.[ns] || {};
    const brandNS =
      brandTranslations[lng]?.[ns] || brandTranslations["default"]?.[ns] || {};

    const mergedTranslation = isExactMatch
      ? { ...defaultNS, ...brandNS }
      : { ...brandNS, ...defaultNS };
    i18n.addResourceBundle(lng, ns, mergedTranslation, true, true);
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "fr",
    ns: namespaces,
    defaultNS: "components/intro",
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    saveMissing: false,
  });

export { i18n };
